import { useState, useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useLicense } from '../contexts/LicenseContext'
import { useApi } from '../contexts/ApiContext'

export default function Activation() {
  const { status, refresh } = useLicense()
  const navigate = useNavigate()
  const api = useApi()
  const [dragging, setDragging] = useState(false)
  const [activating, setActivating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Redirect to main app once license is valid
  useEffect(() => {
    if (!status?.isValid) return
    const timer = setTimeout(() => navigate('/', { replace: true }), 600)
    return () => clearTimeout(timer)
  }, [status, navigate])

  const isExpiredOrInvalid = status && !status.isValid && (status.isExpired || status.isActivated)

  const handleActivation = useCallback(
    async (fileContent: string) => {
      setActivating(true)
      setError(null)
      try {
        // If there's an existing expired/invalid license, reset it first
        if (isExpiredOrInvalid) {
          await api.resetLicense()
        }
        const result = await api.activateLicense(fileContent)
        if (result.success) {
          setSuccess(true)
          await refresh()
        } else {
          setError(result.error || 'Activation failed.')
        }
      } catch {
        setError('Failed to process license file.')
      } finally {
        setActivating(false)
      }
    },
    [api, refresh, isExpiredOrInvalid]
  )

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault()
      setDragging(false)
      const file = e.dataTransfer.files[0]
      if (!file) return
      const reader = new FileReader()
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          handleActivation(reader.result)
        }
      }
      reader.readAsText(file)
    },
    [handleActivation]
  )

  const handleBrowse = useCallback(async () => {
    const result = await api.openFileDialog({
      title: 'Select License File',
      filters: [
        { name: 'License Files', extensions: ['tn'] },
        { name: 'All Files', extensions: ['*'] },
      ],
      properties: ['openFile'],
    })
    if (!result || (Array.isArray(result) && result.length === 0)) return
    const filePath = Array.isArray(result) ? result[0] : result
    try {
      const { base64 } = await api.readFile(filePath)
      const content = atob(base64)
      await handleActivation(content)
    } catch {
      setError('Could not read the selected file.')
    }
  }, [api, handleActivation])

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="max-w-md w-full mx-4">
        {/* Header */}
        <div className="text-center mb-8">
          <div
            className="inline-flex items-center justify-center w-16 h-16 rounded-2xl text-white text-2xl font-bold mb-4"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            TN
          </div>
          <h1 className="text-2xl font-semibold text-gray-900">Talent Navigator</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isExpiredOrInvalid ? 'Your license has expired. Activate a new license to continue.' : 'License Activation Required'}
          </p>
        </div>

        {/* Error from license check */}
        {status?.error && !error && (
          <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
            {status.error}
          </div>
        )}

        {/* Drop zone */}
        <div
          onDragOver={(e) => {
            e.preventDefault()
            setDragging(true)
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={`border-2 border-dashed rounded-xl p-8 text-center transition-all ${
            dragging
              ? 'border-purple-400 bg-purple-50'
              : 'border-gray-300 bg-white hover:border-gray-400'
          }`}
        >
          {success ? (
            <div className="py-4">
              <div className="text-4xl mb-3">&#10003;</div>
              <p className="text-lg font-semibold text-green-700">License Activated!</p>
              <p className="text-sm text-gray-500 mt-1">Redirecting to application...</p>
            </div>
          ) : (
            <>
              <div className="text-4xl mb-3 text-gray-300">&#128274;</div>
              <p className="text-sm font-medium text-gray-700 mb-1">
                Drag & drop your license file here
              </p>
              <p className="text-xs text-gray-400 mb-4">or</p>
              <button
                onClick={handleBrowse}
                disabled={activating}
                className="px-6 py-2.5 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
              >
                {activating ? 'Activating...' : 'Browse for License File'}
              </button>
              <p className="text-xs text-gray-400 mt-4">
                Accepts .tn license files provided by your Accenture representative
              </p>
            </>
          )}
        </div>

        {/* Error message */}
        {error && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Help text */}
        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Don&apos;t have a license? Contact your Accenture representative.
          </p>
        </div>
      </div>
    </div>
  )
}
